import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useBackgroundLibrary } from "@/components/Backgrounds/useBackgroundLibrary";
import { useBackgroundRequests } from "@/components/Backgrounds/useBackgroundRequests";
import { PageWrapper } from "@/components/PageWrapper";
import { AdminPromptStudioHeader } from "@/containers/Admin/PromptStudio/AdminPromptStudioHeader";
import { EnvContext } from "@/contexts/appContexts";
import { usePromptStudioScope } from "@/containers/Coach/PromptStudio/PromptStudioScope";
import {
  isTooSmall,
  UPLOAD_TOO_LARGE,
  UPLOAD_TYPES,
} from "@/containers/Coach/PromptStudio/hooks/useBackgroundMutations";
import { isApiError } from "@/services/bedrock/apiError";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { UploadBackgroundRequest } from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";
import { getDownloadURLFirebase, saveElementToFirebaseCancellable } from "@/utils/firebase";
import { AdminGenerateDialog } from "./AdminGenerateDialog";
import { AssetGrid, AssetSelection } from "./AssetGrid";
import { AssetReviewDialog } from "./AssetReviewDialog";
import {
  DEFAULT_LIBRARY_FILTERS,
  LibraryActions,
  LibraryFilters,
  LibraryScope,
  LibraryToolbar,
  librarySearchBody,
} from "./LibraryToolbar";
import { QueueStrip } from "./QueueStrip";
import { SelectionBar } from "./SelectionBar";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const UPLOAD_NODE = "design-backgrounds/global";
const UPLOAD_TOO_SMALL = "That photo is too small. Use one at least 600 px on each side.";
const UPLOAD_FAILED = "That photo couldn't be uploaded. Try again.";
const ASSET_PARAM = "asset";

function assetFromSearch(search: string): number | null {
  const id = Number(new URLSearchParams(search).get(ASSET_PARAM));
  return Number.isInteger(id) && id > 0 ? id : null;
}

interface GenerateState {
  open: boolean;
  prompt?: string;
  scope: LibraryScope;
}

/** Super-admin curation of the shared and per-coach background libraries. */
export const BackgroundLibraryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const envValues = React.useContext(EnvContext);
  // Shared with the Designs tab, so switching tabs keeps the coach.
  const { scope, coach } = usePromptStudioScope();
  const [filters, setFilters] = React.useState<LibraryFilters>(DEFAULT_LIBRARY_FILTERS);
  const [selected, setSelected] = React.useState<AssetSelection>(new Map());
  const [generate, setGenerate] = React.useState<GenerateState>({ open: false, scope: "global" });
  const [uploading, setUploading] = React.useState(false);

  const history = useHistory();
  const location = useLocation();
  // The open asset lives in the URL so a link or a reload lands on it.
  const openAssetId = assetFromSearch(location.search);
  const setOpenAssetId = (id: number | null) => {
    const params = new URLSearchParams(location.search);
    if (id == null) params.delete(ASSET_PARAM);
    else params.set(ASSET_PARAM, String(id));
    const search = params.toString();
    history.replace({ ...location, search: search ? `?${search}` : "" });
  };

  const body = librarySearchBody(filters, scope, coach);
  // The grid's own query (same key, so no extra request), for paging through it in the review view.
  const { assets } = useBackgroundLibrary(body);
  const openIndex = openAssetId == null ? -1 : assets.findIndex(asset => asset.id === openAssetId);
  const { pending } = useBackgroundRequests({ scope: "all", coachId: scope === "coach" ? coach?.id : undefined });
  const pendingNew = pending.filter(request => request.backgroundAssetId === null);

  // A selection belongs to one library.
  React.useEffect(() => setSelected(new Map()), [scope, coach?.id]);

  const upload = async (file: File) => {
    if (!UPLOAD_TYPES.includes(file.type) || file.size > MAX_UPLOAD_BYTES) {
      toast.error(UPLOAD_TOO_LARGE);
      return;
    }
    setUploading(true);
    try {
      let url: string;
      try {
        // The Firebase helper appends the extension from the file type.
        const filename = uuidv4();
        await saveElementToFirebaseCancellable(envValues, UPLOAD_NODE, file, filename);
        url = await getDownloadURLFirebase(envValues, UPLOAD_NODE, file, filename);
      } catch {
        toast.error(UPLOAD_FAILED);
        return;
      }
      const request: UploadBackgroundRequest =
        scope === "global" ? { url, scope: "global", visibility: "draft" } : { url, coachId: coach!.id };
      try {
        const asset = await BackgroundAssetServices.upload(request);
        queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.all });
        setOpenAssetId(asset.id);
      } catch (error) {
        toast.error(isApiError(error, "validation_error") && isTooSmall(error) ? UPLOAD_TOO_SMALL : UPLOAD_FAILED);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <PageWrapper hideSeparator fitViewport className="flex min-h-0 flex-1 flex-col gap-0">
      <AdminPromptStudioHeader
        tab="backgrounds"
        actions={
          <LibraryActions
            canWrite={scope === "global" || coach !== null}
            onGenerate={() => setGenerate({ open: true, scope })}
            onUpload={file => void upload(file)}
            uploading={uploading}
          />
        }
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-6" data-testid="background-library">
        <LibraryToolbar filters={filters} onFiltersChange={setFilters} />
        <QueueStrip />
        <AssetGrid
          body={body}
          previewRatio={filters.previewRatio}
          selected={selected}
          onSelectedChange={setSelected}
          onOpen={setOpenAssetId}
          pendingRequests={pendingNew}
        />
        <SelectionBar selected={selected} scope={scope} onSelectedChange={setSelected} />
      </div>

      <AssetReviewDialog
        assetId={openAssetId}
        onClose={() => setOpenAssetId(null)}
        onOpenAsset={setOpenAssetId}
        onNewFromPrompt={prompt => setGenerate({ open: true, prompt, scope: "global" })}
        previousId={openIndex > 0 ? assets[openIndex - 1].id : null}
        nextId={openIndex >= 0 && openIndex < assets.length - 1 ? assets[openIndex + 1].id : null}
        position={openIndex >= 0 ? { index: openIndex + 1, total: assets.length } : null}
      />
      <AdminGenerateDialog
        open={generate.open}
        onOpenChange={open => setGenerate(current => ({ ...current, open }))}
        coach={coach}
        initialScope={generate.scope}
        initialPrompt={generate.prompt}
      />
    </PageWrapper>
  );
};
